import { Injectable } from '@angular/core';
import type {
  AuthUser
} from '@ppl/auth';
import {
  AuthService,
  getLoggedInUser,
  getLoggedInUserProfile
} from '@ppl/auth';
import type {
  ChangeUserProfilePasswordInput,
  ChangeUserProfilePasswordPayload,
  Query,
  RemoveUserProfilePictureInput,
  RemoveUserProfilePicturePayload,
  SetUserProfilePictureInput,
  SetUserProfilePicturePayload,
  UpdateUserProfileInput,
  UpdateUserProfilePayload,
  UpdateUserSettingInput
} from '@ppl/graphql-user-api';
import {
  changeUserProfilePasswordMutation,
  updateUserSettingMutation
} from '@ppl/graphql-user-api';
import { PplDialogService } from '@ppl/ui/dialog';
import { apolloClean } from '@ppl/utils/apollo.utils';
import type { DialogResponse } from '@ppl/utils/dialog.utils';
import { gql } from '@ppl/utils/graphql.utils';
import { EMPTY, of } from 'rxjs';
import {
  delay,
  map,
  switchMap,
  tap
} from 'rxjs/operators';
import { CoreStore } from '../../../core/store/core.store';
import type { PasswordChangeDialogData, PasswordChangeDialogResponse } from '../../../shared/components/password-change-dialog/password-change-dialog.component';
import { PasswordChangeDialogComponent } from '../../../shared/components/password-change-dialog/password-change-dialog.component';
import type { PasswordChangeFormData } from '../../../shared/components/password-change/password-change.component';
import { LoginService } from '../../login/services/login.service';

const removeUserProfilePictureMutation = gql`
  mutation removeUserProfilePicture($input: RemoveUserProfilePictureInput!) {
    removeUserProfilePicture(input: $input) {
      clientMutationId
    }
  }
`;

const setUserProfilePictureMutation = gql`
  mutation setUserProfilePicture($input: SetUserProfilePictureInput!) {
    setUserProfilePicture(input: $input) {
      clientMutationId
    }
  }
`;

const updateUserProfileMutation = gql`
  mutation updateUserProfile($input: UpdateUserProfileInput!,$validationLevel: [ValidationLevel!]) {
    updateUserProfile(input: $input,validationLevel: $validationLevel) {
      clientMutationId
    }
  }
`;

@Injectable({
  providedIn: 'root'
})
export class UserProfileService {

  constructor(
    private store: CoreStore,
    private authService: AuthService,
    private loginService: LoginService,
    private dialogService: PplDialogService
  ) { }

  updatePassword(values: ChangeUserProfilePasswordInput) {
    const input = apolloClean(values);
    return this.authService.gqlClient.mutate<{ changeUserProfilePassword: ChangeUserProfilePasswordPayload; }>({
      mutation: changeUserProfilePasswordMutation,
      variables: { input }
    }).pipe(
      switchMap(({ data }) => of(data.changeUserProfilePassword.result))
    );
  }


  updatePreferences(values: UpdateUserSettingInput) {
    const input = apolloClean(values);
    return this.authService.gqlClient.mutate({
      mutation: updateUserSettingMutation,
      variables: { input }
    }).pipe(
      tap(() => this.store.dispatch('Auth_UpdateSettings', input)),
      delay(100),
      tap(() => this.loginService.notifyUserChanged().subscribe()),
      delay(300)
    );
  }

  updateProfile(values: UpdateUserProfileInput) {
    const input = apolloClean(values);
    return this.authService.gqlClient.mutate<{ updateUserProfile: UpdateUserProfilePayload; }>({
      mutation: updateUserProfileMutation,
      variables: { input }
    }).pipe(
      tap(() => this.store.dispatch('Auth_UpdateProfile', input))
    );
  }

  removeProfilePicture() {
    const userProfile = this.store.get(getLoggedInUserProfile);
    const input: RemoveUserProfilePictureInput = {
      id: userProfile.id
    };
    this.authService.gqlClient.mutate<{ removeUserProfilePicture: RemoveUserProfilePicturePayload; }>({
      mutation: removeUserProfilePictureMutation,
      variables: { input },
      context: {
        retry: false
      }
    }).subscribe(({ data }) => {
      // optimistic approach
    });
  }

  setProfilePicture(picture: string) {
    const userProfile = this.store.get(getLoggedInUserProfile);
    const input: SetUserProfilePictureInput = {
      id: userProfile.id,
      data: picture.split(',')[1]
    };

    this.authService.gqlClient.mutate<{ setUserProfilePicture: SetUserProfilePicturePayload; }>({
      mutation: setUserProfilePictureMutation,
      variables: { input },
      context: {
        retry: false
      }
    }).subscribe(({ data }) => {
      // optimistic approach
      this.store.dispatch('Auth_UpdateProfile', data.setUserProfilePicture.result);
    });
  }

  fetchLastPasswordChange() {
    return this.authService.gqlClient.query<Query>({
      query: gql`
        query fetchLastPasswordChange {
          profile {
            passwordChangedAt
          }
        }
      `
    }).pipe(
      map(({ data }) => {
        return data.profile.passwordChangedAt;
      })
    );
  }

  changeProfilePassword() {
    return this.dialogService.open<
      PasswordChangeDialogComponent,
      PasswordChangeDialogData,
      DialogResponse<PasswordChangeDialogResponse>
    >(PasswordChangeDialogComponent, {
      data: {
        save: (formValues: PasswordChangeFormData) => {
          const storeUser: AuthUser = this.store.get(getLoggedInUser);
          const changePasswordInput: ChangeUserProfilePasswordInput = {
            id: storeUser.profile.id,
            oldPassword: formValues.currentPassword,
            newPassword: formValues.password1
          };
          return this.updatePassword(changePasswordInput).pipe(
            switchMap(success => {
              if (success) {
                return of(success);
              } else {
                this.dialogService.alert({
                  text: `Current password is incorrect. Password wasn't changed.`
                });
                return EMPTY;
              }
            })
          );
        }
      }
    });
  }
}
